import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, RefreshCw, Lock, Rocket } from "lucide-react";

const TONE = {
  NOT_CONFIGURED: "text-slate-600 bg-slate-100",
  CONFIGURATION_IN_PROGRESS: "text-amber-800 bg-amber-100",
  READY_FOR_TESTING: "text-blue-800 bg-blue-100",
  PILOT_READY: "text-indigo-800 bg-indigo-100",
  PRODUCTION_READY: "text-emerald-800 bg-emerald-100",
};

export default function RecoverySetupWizard() {
  const { mcId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activating, setActivating] = useState(false);

  const load = useCallback(async () => {
    if (!mcId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/recovery/readiness/${mcId}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [mcId]);
  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    setActivating(true);
    try {
      const { data: res } = await api.post(`/recovery/activate/${mcId}`, {
        effective_date: new Date().toISOString().slice(0, 10),
        billing_mode: "SHADOW",
        pilot_signoff: "Ops", operations_signoff: "Ops",
        finance_signoff: "Finance", security_signoff: "Security",
      });
      toast.success(`Activated ${res.billing_mode} @ ${res.activated_at}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Activate failed"); }
    finally { setActivating(false); }
  };

  if (!data) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl space-y-4" data-testid="recovery-setup-wizard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Customer 360 · Recovery Configuration</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 mt-1">Setup wizard</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Readiness</div>
          <div className="text-3xl font-heading font-semibold mt-1">
            {data.ready}/{data.total}
          </div>
        </div>
        <div className="flex-1">
          <div className={"inline-block text-[11px] px-2 py-1 rounded font-medium " + (TONE[data.status] || "")}
            data-testid="setup-status">
            {data.status.replace(/_/g, " ")}
          </div>
          {data.activated && (
            <div className="mt-2 text-[11px] text-emerald-700">
              Activated on {data.activation?.activated_at?.slice(0, 10)} in {data.activation?.billing_mode} mode
            </div>
          )}
        </div>
        <Button variant="outline" onClick={load} disabled={busy}>
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Refresh
        </Button>
        <Button onClick={activate}
          disabled={activating || !["PILOT_READY", "PRODUCTION_READY"].includes(data.status) || data.activated}
          data-testid="setup-activate"
          className="bg-emerald-700 hover:bg-emerald-800 text-white">
          {data.activated ? <Lock className="h-4 w-4 mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5" />}
          {data.activated ? "Activated" : "Activate"}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y" data-testid="setup-checks">
        {data.checks.map((c) => (
          <div key={c.key} className="flex items-center gap-3 px-4 py-2"
            data-testid={`setup-check-${c.key}`}>
            {c.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                  : <XCircle className="h-5 w-5 text-slate-300" />}
            <span className={"flex-1 text-[13px] " + (c.ok ? "text-slate-900" : "text-slate-500")}>
              {c.key.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
            {c.count != null && (
              <span className="text-[11px] text-slate-500 font-mono">×{c.count}</span>
            )}
            {c.note && <span className="text-[10px] text-slate-400 italic">{c.note}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
